import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { progressApi } from '@/api/progress';
import { vocabApi, type UserVocabEntry, type WordSense, type WordForms } from '@/api/vocab';
import type { CEFRLevel } from '@/api/courses';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { AudioPlayButton } from '@/components/ui/AudioPlayButton';
import { preloadWords, rememberAudio } from '@/lib/wordAudio';
import { useAuthStore } from '@/stores/auth';
import { StudentTopBar } from '@/components/layout/StudentTopBar';
import { VocabSidebar } from './desktop/VocabSidebar';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MBlock, MChips, MHero, MRow, MTag, MTitle } from '@/components/mobile/MobileKit';
import { humanizeError } from '@/lib/errors';

const FILTER_IDS = ['all', 'favorites', 'learned', 'by_level'] as const;

/** Способы тренировки для телефона. Порядок тот же, что в правой колонке. */
const MOBILE_MODES: { to: string; icon: string; title: string; key?: string }[] = [
  { to: '/app/cue-cards/my', icon: 'done_all', title: 'vocab.modeAll' },
  { to: '/app/cue-cards/my?mode=card', icon: 'style', title: 'vocab.modeCards', key: 'card' },
  { to: '/app/cue-cards/my?mode=choose', icon: 'fact_check', title: 'vocab.modeChoose', key: 'choose' },
  { to: '/app/cue-cards/my?mode=sentence', icon: 'edit_note', title: 'vocab.modeSentence', key: 'sentence' },
  { to: '/app/cue-cards/my?mode=audio', icon: 'headphones', title: 'vocab.modeAudio', key: 'audio' },
  { to: '/app/cue-cards/my?mode=type', icon: 'spellcheck', title: 'vocab.modeType', key: 'type' },
];
type FilterId = (typeof FILTER_IDS)[number];

const CEFR_LEVELS: CEFRLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export function StudentVocabularyPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const user = useAuthStore((st) => st.user);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<FilterId>('all');
  const [level, setLevel] = useState<CEFRLevel>('A2');
  const [newWord, setNewWord] = useState('');
  // Какое слово раскрыто в мобильном списке. На десктопе карточки видны все
  // сразу — там раскрывать нечего.
  const [openWord, setOpenWord] = useState<string | null>(null);
  // Word pending deletion — shown in a confirmation modal so a tap doesn't
  // silently wipe a word from the list.
  const [toDelete, setToDelete] = useState<UserVocabEntry | null>(null);

  const stats = useQuery({ queryKey: ['vocab', 'stats'], queryFn: vocabApi.getStats });
  // Тот же ключ, что на главной, — второго запроса не будет.
  const streak = useQuery({ queryKey: ['student', 'stats'], queryFn: progressApi.getMyStats });

  const PAGE = 20;
  const list = useInfiniteQuery({
    queryKey: ['vocab', 'list', filter === 'by_level' ? level : filter, level],
    queryFn: ({ pageParam = 0 }) =>
      vocabApi.listPage(
        filter === 'by_level' ? level : undefined,
        PAGE,
        pageParam as number,
        filter === 'favorites' || filter === 'learned' ? filter : undefined,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE ? undefined : allPages.length * PAGE,
  });

  // ⚠️ ОТБОР ИДЁТ НА СЕРВЕРЕ, ЗДЕСЬ ТОЛЬКО СКЛЕЙКА СТРАНИЦ. Раньше отсев был
  // тут, среди загруженных двадцати слов: у ученика с сорока словами вкладка
  // «Выучено» показывала часть, а счётчик рядом считал все.
  const entries = (list.data?.pages ?? []).flat();

  // Произношения готовим ЗАРАНЕЕ, при открытии раздела: нажатие должно
  // проигрывать готовое, а не ждать ответа сервера.
  useEffect(() => {
    if (entries.length === 0) return;
    rememberAudio(Object.fromEntries(entries.map((e) => [e.word.word, e.word.audio_url])));
    void preloadWords(entries.map((e) => e.word.word));
  }, [entries]);

  // Infinite-scroll sentinel — load the next 20 when it scrolls into view.
  // Метка догрузки: на телефоне это пункт списка, на компьютере — строка
  // блока, поэтому тип общий.
  const sentinelRef = useRef<HTMLElement | null>(null);
  const loadMore = useCallback(() => {
    if (list.hasNextPage && !list.isFetchingNextPage) void list.fetchNextPage();
  }, [list]);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => e[0]?.isIntersecting && loadMore());
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  /**
   * Слово добавлено — показываем галочку на пару секунд.
   *
   * 🔴 ЗАЧЕМ. Кнопка блокировалась на время запроса, но выглядела так же, и
   * человек не понимал, идёт работа или нет. Жали дважды — и в словаре
   * появлялось два одинаковых слова, а школа дважды платила за разбор и озвучку
   * (05.09.2026). Теперь видно и работу, и её конец.
   */
  const [justAdded, setJustAdded] = useState(false);
  const addWord = useMutation({
    mutationFn: (w: string) => vocabApi.addToMyList(w),
    onSuccess: () => {
      setNewWord('');
      setJustAdded(true);
      window.setTimeout(() => setJustAdded(false), 1800);
      qc.invalidateQueries({ queryKey: ['vocab'] });
    },
  });

  const toggleFav = useMutation({
    mutationFn: (id: string) => vocabApi.toggleFavorite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vocab', 'list'] }),
  });

  const removeWord = useMutation({
    mutationFn: (id: string) => vocabApi.deleteWord(id),
    onSuccess: () => {
      setToDelete(null);
      qc.invalidateQueries({ queryKey: ['vocab'] });
    },
  });

  // ── Телефон: макет 23.08.2026 ────────────────────────────────────────────
  // Оранжевый блок — единственное действие экрана (повторить слова). Фильтры
  // лентой фишек, уровни второй лентой. Слова одним тонированным блоком:
  // состояние словом справа, а не цветной плашкой на каждой строке.
  if (isMobile) {
    const st = stats.data;
    return (
      <div className="space-y-3.5">
        <MTitle
          title={t('nav.vocabulary')}
          subtitle={t('vocab.subtitleShort', { total: st?.total ?? 0, due: st?.due_today ?? 0 })}
        />

        <MHero
          mascot="reading"
          title={String(st?.due_today ?? 0)}
          eyebrow={t('vocab.due')}
          subtitle={t('vocab.learnedOf', { learned: st?.learned ?? 0, total: st?.total ?? 0 })}
          action={t('vocab.reviewAction')}
          to="/app/cue-cards/my"
        />

        {/* Добавление слова. В первой версии мобильной ветки его не было вовсе —
            заказчик 23.08.2026: «а где добавить слова?». Одна строка вместо
            карточки: перевод и примеры платформа находит сама. */}
        <div className="flex items-center gap-2 rounded-pill bg-surface-container-low px-3 py-2">
          <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-primary-container text-primary">
            <span className="material-symbols-outlined text-[16px]">add</span>
          </span>
          <input
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={(e) => {
                // ⚠️ Проверка «уже отправляем» обязательна: двойной Enter слал
                // слово дважды ровно так же, как двойное нажатие кнопки.
                if (e.key === 'Enter' && newWord.trim() && !addWord.isPending) {
                  addWord.mutate(newWord.trim());
                }
            }}
            placeholder={t('vocab.addWordPlaceholder')}
            aria-label={t('vocab.add')}
            className="min-w-0 flex-1 bg-transparent text-[13px] text-on-surface outline-none placeholder:text-on-surface-variant"
          />
            {(newWord.trim() || addWord.isPending || justAdded) && (
              <button
                type="button"
                onClick={() => addWord.mutate(newWord.trim())}
                disabled={addWord.isPending || justAdded || !newWord.trim()}
                className="flex flex-none items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-[12px] font-bold text-on-primary disabled:opacity-70"
              >
                {addWord.isPending && (
                  <span className="material-symbols-outlined animate-spin text-[15px]">progress_activity</span>
                )}
                {justAdded && <span className="material-symbols-outlined text-[15px]">check</span>}
                {addWord.isPending ? t('vocab.adding') : justAdded ? t('vocab.added') : t('vocab.add')}
              </button>
            )}
        </div>
        {addWord.isError && <p className="px-1 text-xs text-error">{humanizeError(addWord.error)}</p>}

        {/* ⚠️ СПОСОБЫ НУЖНЫ И НА ТЕЛЕФОНЕ. Приложение у нас мобильное, а до
            06.09.2026 выбор способа был только в правой колонке — то есть
            только на компьютере. С телефона ученик мог лишь «всё подряд».
            Слово выучено, когда закрыты все пять; выбор способа — как раз то,
            чем он этот путь сокращает. */}
        {/* ⚠️ `w-max` И `overflow-x-auto` НА ОДНОМ ЭЛЕМЕНТЕ — ЭТО НЕ ЛЕНТА, А
            РАСПОРКА. `w-max` растягивает контейнер по содержимому, то есть
            ШИРЕ ЭКРАНА; прокручивать внутри себя ему уже нечего, и свайп
            уносит вбок ВСЮ страницу. Заказчик 07.09.2026 показал это снимком:
            листнул ленту тренажёров, а уехал весь словарь, оставив пустое поле.

            Правильно так: внешняя обёртка прокручивается, внутренний ряд
            растянут. Тогда палец двигает только ленту.

            `-mx-5 px-5` — чтобы лента шла от края до края, но первая и
            последняя таблетки не липли к границе экрана. */}
        <div className="-mx-5 overflow-x-auto px-5 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex w-max gap-1.5">
          {MOBILE_MODES.map((m) => (
            <Link
              key={m.to}
              to={m.to}
              className="flex flex-none items-center gap-1.5 rounded-pill bg-surface-container-low px-3 py-2 text-[12.5px] font-semibold text-on-surface"
            >
              <span className="material-symbols-outlined text-[15px] text-primary">{m.icon}</span>
              {t(m.title)}
              {/* Освоено из всех слов — то же число, что в правой колонке. */}
              <span className="tabular-nums text-on-surface-variant">
                {m.key ? (st?.modes?.[m.key] ?? 0) : (st?.learned ?? 0)}/{st?.total ?? 0}
              </span>
            </Link>
          ))}
          {(st?.learned ?? 0) > 0 && (
            <Link
              to="/app/cue-cards/my?scope=learned"
              className="flex flex-none items-center gap-1.5 rounded-pill bg-info-container px-3 py-2 text-[12.5px] font-semibold text-info"
            >
              <span className="material-symbols-outlined text-[15px]">history</span>
              {t('vocab.reviewLearned')}
            </Link>
          )}
          </div>
        </div>

        <MChips
          chips={FILTER_IDS.map((f) => ({ key: f, label: t(`vocab.filter.${f}`) }))}
          active={filter}
          onChange={(k) => setFilter(k as FilterId)}
        />

        {filter === 'by_level' && (
          <MChips
            chips={CEFR_LEVELS.map((l) => ({ key: l, label: l }))}
            active={level}
            onChange={(k) => setLevel(k as CEFRLevel)}
          />
        )}

        {entries.length === 0 ? (
          <MBlock className="py-5 text-center text-[13px] text-on-surface-variant">
            {list.isLoading ? t('common.loading') : t('vocab.empty')}
          </MBlock>
        ) : (
          <MBlock>
            {entries.map((e, i2) => (
              <div key={e.id}>
                <MRow
                  title={
                    <span className="flex items-baseline gap-2">
                      <span className="font-display text-[15px] font-bold">{e.word.word}</span>
                      <span className="truncate text-[10.5px] font-normal text-on-surface-variant">
                        {e.word.cefr_level}
                      </span>
                    </span>
                  }
                  meta={e.word.translation_ru || e.word.translation_kz}
                  // Нажатие раскрывает слово прямо в списке — как в макете:
                  // значения, примеры, произношение. Уводить на отдельный экран
                  // ради двух строк перевода незачем.
                  onClick={() => setOpenWord((cur) => (cur === e.id ? null : e.id))}
                  trailing={
                    <span className="flex flex-none items-center gap-2">
                      <MTag tone={e.is_learned ? 'info' : e.is_favorite ? 'warm' : 'muted'}>
                        {e.is_learned
                          ? t('vocab.learned')
                          : e.is_favorite
                            ? t('vocab.filter.favorites')
                            : t('vocab.learning')}
                      </MTag>
                      <span className="material-symbols-outlined text-[16px] text-on-surface-variant">
                        {openWord === e.id ? 'expand_less' : 'expand_more'}
                      </span>
                    </span>
                  }
                  last={openWord === e.id || i2 === entries.length - 1}
                />
                {openWord === e.id && (
                  <div className={i2 === entries.length - 1 ? 'pb-3' : 'border-b border-outline pb-3'}>
                    <WordCard
                      entry={e}
                      onToggleFav={() => toggleFav.mutate(e.id)}
                      onDelete={() => setToDelete(e)}
                    />
                  </div>
                )}
              </div>
            ))}
          </MBlock>
        )}

        {/* Подгрузка следующих двадцати. Без неё словарь на телефоне
            заканчивался на первой странице. */}
        {list.hasNextPage && (
          <button
            type="button"
            onClick={() => void list.fetchNextPage()}
            disabled={list.isFetchingNextPage}
            className="w-full rounded-pill bg-surface-container-low py-3 text-[13px] font-semibold text-on-surface-variant"
          >
            {list.isFetchingNextPage ? t('common.loading') : t('vocab.loadMore')}
          </button>
        )}

        {/* ⚠️ Окно удаления обязано быть и здесь: кнопка «удалить» в карточке
            слова открывала окно, которого в дереве не существовало. */}
        <Modal
          open={!!toDelete}
          onClose={() => !removeWord.isPending && setToDelete(null)}
          title={t('vocab.deleteTitle')}
          size="sm"
        >
          <p className="text-sm text-on-surface-variant">
            {t('vocab.deleteConfirm', { word: toDelete?.word.word })}
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setToDelete(null)} disabled={removeWord.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={removeWord.isPending}
              onClick={() => toDelete && removeWord.mutate(toDelete.id)}
            >
              {t('common.delete')}
            </Button>
          </div>
        </Modal>
      </div>
    );
  }

  const total = stats.data?.total ?? 0;
  const learned = stats.data?.learned ?? 0;
  const due = stats.data?.due_today ?? 0;

  return (
    <>
      <StudentTopBar
        title={t('vocab.title')}
        subtitle={t('vocab.subtitle')}
        streak={streak.data?.current_streak ?? 0}
        name={user?.full_name}
        avatarUrl={user?.avatar_url}
      />

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          {/* ── К повторению ────────────────────────────────────────────── */}
          <div className="flex items-center gap-[18px] rounded-[24px] bg-info-container px-6 py-5">
            <div className="min-w-0 flex-1">
              <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-info">
                {t('vocab.readyForReview')}
              </div>
              <div className="my-[10px] mb-[5px] font-display text-[30px] font-bold leading-[1.1] text-on-surface">
                {t('vocab.wordsCount', { count: due })}
              </div>
              <p className="text-[13px] text-on-surface-variant">
                {due > 0 ? t('vocab.reviewHint') : t('vocab.reviewNothing')}
              </p>
            </div>
            <Link
              to="/app/cue-cards/my"
              className="inline-flex flex-none items-center gap-2 rounded-full bg-info px-[22px] py-[13px] text-[14.5px] font-bold text-white transition hover:brightness-110"
            >
              {t('vocab.learnWithCards')}
              <span className="material-symbols-outlined text-[17px]">arrow_forward</span>
            </Link>
          </div>

          {/* ── Отборы ──────────────────────────────────────────────────── */}
          <div className="my-5 mb-4 flex w-max gap-1 rounded-full bg-surface-container p-1">
            {FILTER_IDS.map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => setFilter(id)}
                className={cn(
                  'rounded-full px-5 py-[9px] text-[12.5px] font-semibold transition',
                  filter === id
                    ? 'bg-primary text-on-primary'
                    : 'text-on-surface-variant hover:bg-surface-container-high',
                )}
              >
                {t(`vocab.filter_${id}`)}
                {id === 'all' && total > 0 ? ` ${total}` : ''}
                {id === 'learned' && learned > 0 ? ` ${learned}` : ''}
              </button>
            ))}
          </div>

          {filter === 'by_level' && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="text-[11px] uppercase tracking-label text-on-surface-variant">
                {t('vocab.cefrFilterLabel')}
              </span>
              {CEFR_LEVELS.map((lvl) => (
                <button
                  key={lvl}
                  type="button"
                  onClick={() => setLevel(lvl)}
                  className={cn(
                    'rounded-full px-3 py-1 text-[12px] font-semibold transition',
                    level === lvl
                      ? 'bg-primary text-on-primary'
                      : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high',
                  )}
                >
                  {lvl}
                </button>
              ))}
            </div>
          )}

          {toggleFav.isError && <p className="mb-4 text-xs text-error">{humanizeError(toggleFav.error)}</p>}

          {/* ── Список слов ─────────────────────────────────────────────── */}
          {list.isLoading ? (
            <div className="h-40 animate-pulse rounded-[20px] bg-surface-container" />
          ) : list.isError ? (
            <div className="space-y-3 rounded-[20px] bg-surface-container px-[18px] py-4 text-center text-on-surface-variant">
              <p className="text-error">{humanizeError(list.error)}</p>
              <Button variant="secondary" size="sm" onClick={() => list.refetch()}>
                {t('vocab.retry')}
              </Button>
            </div>
          ) : entries.length === 0 ? (
            <div className="rounded-[20px] bg-surface-container px-[18px] py-6 text-center text-[13px] text-on-surface-variant">
              {filter === 'favorites'
                ? t('vocab.emptyFavorites')
                : filter === 'learned'
                  ? t('vocab.emptyLearned')
                  : filter === 'by_level'
                    ? t('vocab.emptyByLevel', { level })
                    : t('vocab.emptyList')}
            </div>
          ) : (
            <div className="rounded-[20px] bg-surface-container px-[18px] py-0.5">
              {entries.map((entry) => (
                <WordCard
                  key={entry.id}
                  entry={entry}
                  compact
                  onToggleFav={() => toggleFav.mutate(entry.id)}
                  onDelete={() => setToDelete(entry)}
                />
              ))}
              {list.hasNextPage && <div ref={(el) => { sentinelRef.current = el; }} className="h-4" aria-hidden />}
              {list.isFetchingNextPage && (
                <p className="py-2 text-center text-[12.5px] text-on-surface-variant">{t('common.loading')}</p>
              )}
            </div>
          )}
        </div>

        {/* ── Правая колонка ───────────────────────────────────────────── */}
        <VocabSidebar
          total={total}
          learned={learned}
          modes={stats.data?.modes ?? {}}
          learning={stats.data?.learning ?? 0}
          fresh={stats.data?.fresh ?? 0}
          newWord={newWord}
          onNewWord={setNewWord}
          onAdd={() => addWord.mutate(newWord.trim())}
          adding={addWord.isPending}
          justAdded={justAdded}
          addError={addWord.isError ? humanizeError(addWord.error) : undefined}
        />
      </div>

      <Modal
        open={!!toDelete}
        onClose={() => !removeWord.isPending && setToDelete(null)}
        title={t('vocab.deleteTitle')}
        size="sm"
      >
        <p className="text-sm text-on-surface-variant">
          {t('vocab.deleteConfirm', { word: toDelete?.word.word })}
        </p>
        {removeWord.isError && (
          <p className="mt-2 text-xs text-error">{humanizeError(removeWord.error)}</p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setToDelete(null)} disabled={removeWord.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            loading={removeWord.isPending}
            onClick={() => toDelete && removeWord.mutate(toDelete.id)}
          >
            {t('common.delete')}
          </Button>
        </div>
      </Modal>
    </>
  );
}

/**
 * Части речи и формы слова — КЛЮЧИ перевода, а не готовый текст.
 *
 * ⚠️ Раньше здесь лежали русские сокращения («сущ.», «прош.»), и они оставались
 * русскими на казахском и английском интерфейсе. Заказчик поймал это 24.08.2026:
 * «интерфейс на английском, а кнопки на русском — язык не свапается».
 */
const POS_KEYS = ['noun','verb','adjective','adverb','phrase','pronoun','preposition','conjunction'] as const;

/**
 * Часть речи словами языка интерфейса.
 *
 * ⚠️ Ключ, которого нет в переводах, i18next возвращает КАК ЕСТЬ. Из-за этого
 * в компактной строке словаря 02.09.2026 писалось «verb» и «noun» на любом
 * языке: я передал в перевод сырое значение с сервера. Незнакомую часть речи
 * показываем как пришла — это лучше пустоты.
 */
function posLabel(pos: string, t: (k: string) => string): string {
  const key = pos.toLowerCase();
  return POS_KEYS.includes(key as (typeof POS_KEYS)[number]) ? t(`vocab.pos.${key}`) : pos;
}

const FORM_KEYS: Array<keyof WordForms> = [
  'past', 'past_participle', 'present_participle', 'third_person',
  'plural', 'comparative', 'superlative',
];

function formChips(
  forms: WordForms | undefined,
  label: (key: string) => string,
): Array<{ label: string; value: string }> {
  if (!forms) return [];
  return FORM_KEYS.flatMap((key) => {
    const value = forms[key]?.trim();
    return value ? [{ label: label(`vocab.form.${key}`), value }] : [];
  });
}


// One meaning row inside a word card: number badge · POS · per-sense IPA · RU/KK
// translation · simple definition · example (EN + RU).
function SenseRow({ sense, index }: { sense: WordSense; index: number }) {
  const { t } = useTranslation();
  const pos = sense.pos?.trim();
  const ru = sense.translation_ru?.trim();
  const kk = sense.translation_kk?.trim();
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-fixed text-[11px] font-bold tabular-nums text-primary">
        {index + 1}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {pos && (
            <span className="rounded-pill bg-surface-container-low px-2 py-0.5 text-[10px] font-semibold uppercase tracking-label text-on-surface-variant">
              {posLabel(pos, t)}
            </span>
          )}
          {sense.ipa?.trim() && (
            <span className="font-mono text-xs text-on-surface-variant/80">{sense.ipa.trim()}</span>
          )}
          <span className="font-semibold text-on-surface">{ru || kk || '—'}</span>
          {ru && kk && <span className="text-xs text-on-surface-variant/70">· {kk}</span>}
        </div>
        {sense.definition?.trim() && (
          <p className="mt-0.5 text-xs leading-snug text-on-surface-variant/80">{sense.definition.trim()}</p>
        )}
        {sense.example?.trim() && (
          <p className="mt-1 text-xs italic leading-snug text-on-surface">
            “{sense.example.trim()}”
            {sense.example_ru?.trim() && (
              <span className="not-italic text-on-surface-variant/70"> — {sense.example_ru.trim()}</span>
            )}
          </p>
        )}
      </div>
    </li>
  );
}

function WordCard({
  entry,
  onToggleFav,
  onDelete,
  compact,
}: {
  entry: UserVocabEntry;
  onToggleFav: () => void;
  onDelete: () => void;
  /** Строка списка по макету «Desktop Web v2»: разбор раскрывается по нажатию. */
  compact?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(!compact);
  const w = entry.word;
  const senses = w.senses ?? [];
  const chips = formChips(w.forms, (k) => t(k));
  // ⚠️ ГОЛОСА БРАУЗЕРА ЗДЕСЬ БОЛЬШЕ НЕТ. Раньше слово без своего аудио
  // озвучивалось системным синтезом — тем самым, ради ухода от которого
  // серверную озвучку и делали: каждое пятое слово в базе звучало чужим
  // голосом, и одно слово в уроке и в словаре звучало по-разному. Теперь всё
  // идёт общим проигрывателем: нет своего аудио — сервер синтезирует нашим
  // голосом и запомнит навсегда (см. lib/wordAudio.ts).
  //
  // Само нажатие живёт в AudioPlayButton — там же значок загрузки и повтор.
  /**
   * Разбор слова: значения, формы, произношение, избранное, удаление.
   *
   * Вынесен отдельно, потому что на компьютере строка списка идёт по макету —
   * узкой строкой, — и разбор раскрывается под ней. Прятать его совсем было бы
   * потерей: многозначные слова и формы мы делали отдельной фичей.
   */
  const details = (
    <>
      {senses.length > 0 ? (
        <ol className="mt-3 space-y-2.5">
          {senses.map((sense, i) => (
            <SenseRow key={i} sense={sense} index={i} />
          ))}
        </ol>
      ) : (
        <>
          {w.explanation?.trim() && (
            <p className="mt-1 text-xs leading-snug text-on-surface-variant/80">{w.explanation.trim()}</p>
          )}
          {w.example && (
            <p className="mt-3 rounded-button bg-surface-container-low p-3 text-sm italic text-on-surface">
              “{w.example}”
            </p>
          )}
        </>
      )}

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <span
              key={c.label}
              className="inline-flex items-baseline gap-1 rounded-pill bg-surface-container-low px-2.5 py-1 text-xs text-on-surface"
            >
              <span className="text-[10px] uppercase tracking-label text-on-surface-variant/70">{c.label}</span>
              <span className="font-semibold">{c.value}</span>
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {/*
          Кнопка общая: значок ожидания при загрузке и повтор при ошибке —
          те же, что в наборе слов, в карточках и у слова дня.
        */}
        <AudioPlayButton word={w.word} withLabel />
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleFav}
          aria-pressed={entry.is_favorite}
          aria-label={t('vocab.toggleFavourite')}
        >
          <span
            className={cn(
              'material-symbols-outlined transition-colors',
              entry.is_favorite ? 'text-primary' : 'text-on-surface-variant',
            )}
            style={entry.is_favorite ? { fontVariationSettings: "'FILL' 1" } : undefined}
          >
            favorite
          </span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto text-error"
          onClick={onDelete}
          aria-label={t('vocab.deleteWordAriaLabel')}
        >
          <span className="material-symbols-outlined">delete</span>
        </Button>
      </div>
    </>
  );

  if (compact) {
    const ipa = senses.find((x) => x.ipa)?.ipa;
    const pos = senses[0]?.pos;
    const meaning =
      senses[0]?.translation_ru?.trim() ||
      w.translation_ru?.trim() ||
      w.translation_kz?.trim() ||
      t('vocab.translationLoading');
    const due = entry.next_review_at ? new Date(entry.next_review_at) : undefined;

    return (
      <div className="border-b border-outline py-3 last:border-b-0">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-3 text-left">
          {/* Плитка слева: выучено — синяя с галочкой, ещё нет — песочная с
              уровнем слова. Проценты освоения мы не считаем, и рисовать их
              было бы выдумкой. */}
          <span
            className={cn(
              'grid h-[34px] w-[34px] flex-none place-items-center rounded-xl text-[12px] font-bold',
              entry.is_learned ? 'bg-info-container text-info' : 'bg-surface-container-high text-on-surface-variant',
            )}
          >
            {entry.is_learned ? <span className="material-symbols-outlined text-[16px]">check</span> : (w.cefr_level ?? '—')}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline gap-[9px]">
              <span className="truncate text-[14px] font-bold text-on-surface">{w.word}</span>
              {ipa && <span className="flex-none text-[11px] text-on-surface-variant">{ipa}</span>}
            </span>
            <span className="mt-0.5 block truncate text-[12.5px] text-on-surface-variant">
              {meaning}
              {pos ? ` · ${posLabel(pos, t)}` : ''}
            </span>
          </span>
          {due && (
            <span className="flex-none text-[11px] text-on-surface-variant">
              {due.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })}
            </span>
          )}
          <span className="grid h-[30px] w-[30px] flex-none place-items-center rounded-full bg-surface-container-high">
            <span className={cn('material-symbols-outlined text-[15px] text-on-surface-variant transition', open && 'rotate-180')}>
              expand_more
            </span>
          </span>
        </button>
        {open && <div className="pl-[46px]">{details}</div>}
      </div>
    );
  }

  return (
    <Card variant="default">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-xl font-bold tracking-display text-on-surface">
              {w.word}
            </h3>
            {senses.length > 1 && (
              <span className="rounded-pill bg-primary-fixed px-2 py-0.5 text-[10px] font-bold uppercase tracking-label text-primary">
                {t('vocab.sensesCount', { count: senses.length })}
              </span>
            )}
          </div>
          {/* Legacy single-line translation only when senses haven't been
              generated yet (older words await the backfill). */}
          {senses.length === 0 && (
            <p className="text-sm font-medium text-on-surface-variant">
              {w.translation_ru?.trim() || w.translation_kz?.trim() || t('vocab.translationLoading')}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {w.cefr_level && <Pill variant="primary">{w.cefr_level}</Pill>}
          {entry.is_learned && <Pill variant="success">{t('vocab.learned')}</Pill>}
        </div>
      </div>

      {details}
    </Card>
  );
}
